import type { OrderShippingLabel } from '@skupreme/api-contracts/core';
import { Badge, Card } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import { formatPackageSize, formatPackageWeight } from '../lib/packages';

export type ShippingLabelsCardProps = {
  labels: OrderShippingLabel[];
  returnLabels?: OrderShippingLabel[];
  isLoading?: boolean;
};

type HistoryEntry = {
  label: OrderShippingLabel;
  isReturn: boolean;
};

function LabelRow({ label, isReturn }: HistoryEntry) {
  const quantity = (label.items || []).reduce(
    (total, item) => total + (item.qty || 0),
    0,
  );
  const carrier = [label.provider?.toUpperCase(), label.service]
    .filter(Boolean)
    .join(' · ');
  const facts = [
    label.shipFrom?.name,
    [label.package, formatPackageSize(label), formatPackageWeight(label)]
      .filter(Boolean)
      .join(' · '),
    quantity > 0 ? `${quantity} items` : undefined,
    label.rate != null
      ? CommonUtility.currencyFormat(label.rate, undefined, {}, 2)
      : undefined,
  ].filter(Boolean);

  return (
    <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-1 sk:border-b sk:border-rule sk:py-3 sk:last:border-b-0">
      <div className="sk:flex sk:min-w-0 sk:items-center sk:justify-between sk:gap-4">
        <span className="sk:flex sk:min-w-0 sk:flex-wrap sk:items-center sk:gap-2">
          <span className="sk:text-dense sk:font-semibold sk:text-fg">
            {carrier || 'Shipping label'}
          </span>
          {isReturn ? <Badge tone="info">Return</Badge> : null}
          {label.mode === 'test' ? <Badge tone="neutral">Test</Badge> : null}
          {label.refunded ? <Badge tone="caution">Refunded</Badge> : null}
        </span>
        <span className="sk:shrink-0 sk:text-dense sk:text-fg-secondary">
          {label.createdAt
            ? DateUtility.formatDate(label.createdAt, DateFormat.dateTime)
            : null}
        </span>
      </div>

      {facts.length > 0 ? (
        <span className="sk:text-xs sk:text-fg-tertiary">
          {facts.join(' · ')}
        </span>
      ) : null}

      {label.tracking_id ? (
        <span className="sk:min-w-0 sk:truncate sk:text-xs sk:text-fg-secondary">
          Tracking # {label.tracking_id}
        </span>
      ) : null}

      {label.refunded && label.updatedAt ? (
        <span className="sk:text-xs sk:text-fg-tertiary">
          Refunded{' '}
          {DateUtility.formatDate(label.updatedAt, DateFormat.dateTime)}
        </span>
      ) : null}
    </div>
  );
}

/** Read-only trail of every label bought for the order. Acting on a label
 *  belongs to its shipment on Shipping. */
export function ShippingLabelsCard({
  labels,
  returnLabels = [],
  isLoading,
}: ShippingLabelsCardProps) {
  const history: HistoryEntry[] = [
    ...labels.map(label => ({ label, isReturn: false })),
    ...returnLabels.map(label => ({ label, isReturn: true })),
  ].sort(
    (left, right) =>
      new Date(left.label.createdAt || 0).getTime() -
      new Date(right.label.createdAt || 0).getTime(),
  );

  return (
    <Card
      title="Label History"
      action={
        history.length > 0 ? (
          <span className="sk:text-dense sk:text-fg-tertiary">
            {history.length}
          </span>
        ) : null
      }
      bodyClassName="sk:flex sk:flex-col sk:px-4 sk:py-1"
    >
      {isLoading && history.length === 0 ? (
        <div className="sk:my-3 sk:h-20 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
      ) : null}

      {history.map(({ label, isReturn }) => (
        <LabelRow
          key={`${isReturn ? 'return' : 'outbound'}-${label.labelId}`}
          label={label}
          isReturn={isReturn}
        />
      ))}

      {!isLoading && history.length === 0 ? (
        <span className="sk:py-3 sk:text-dense sk:text-fg-tertiary">
          No labels bought for this order
        </span>
      ) : null}
    </Card>
  );
}
